import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { getApiUrl } from "../config/api";
import type {
  AppointmentScheduledMessage,
  AppointmentScheduledMessagePreview,
} from "../types/appointmentScheduledMessages";

// Per-row preview lifecycle, keyed by message uuid. Kept across list refetches
// because the same uuid renders the same body (derived from the template +
// appointment data), so re-fetching on every Send Now / Cancel poll would just
// be noise.
export type PreviewState =
  | { status: "loading" }
  | { status: "ready"; data: AppointmentScheduledMessagePreview }
  | { status: "error" };

// The hook only needs each row's identity + which appointment it belongs to, so
// both callers can pass their full AppointmentScheduledMessage[] as-is.
type PreviewableMessage = Pick<
  AppointmentScheduledMessage,
  "uuid" | "transaction_uuid"
>;

/**
 * Fetches the fully-rendered SMS body for appointment scheduled messages via the
 * compliance bulk-preview endpoint (`.../scheduled-messages/previews`), which is
 * keyed per `transaction_uuid`. Rows are grouped by transaction so each distinct
 * appointment is fetched once, and the results merge into a single
 * `Record<uuid, PreviewState>`.
 *
 * Shared by the Transaction Modal's Scheduled Reminders panel (one appointment)
 * and the two-way chat "Scheduled messages" drawer (a client's messages across
 * several appointments) so the fetch + preview logic can't drift between them.
 *
 * Fetch-once-per-uuid: a uuid already present in the map is never re-fetched, so
 * repeated list refetches (Send Now / Cancel polling) don't re-hit the network.
 * Pending uuids are flagged `loading` up front so a fast follow-up `messages`
 * change can't fire a duplicate request before the first resolves.
 */
export function useAppointmentScheduledMessagePreviews(
  messages: PreviewableMessage[],
): Record<string, PreviewState> {
  const { fetchWithAuth } = useApp();
  const [previews, setPreviews] = useState<Record<string, PreviewState>>({});

  useEffect(() => {
    const pending = messages.filter((m) => !previews[m.uuid]);
    if (pending.length === 0) return;

    // Mark every pending uuid as loading immediately so the re-run this state
    // write triggers finds an empty `pending` and no second fetch goes out.
    setPreviews((prev) => {
      const next = { ...prev };
      for (const m of pending) {
        if (!next[m.uuid]) next[m.uuid] = { status: "loading" };
      }
      return next;
    });

    // One bulk request per distinct transaction covers all of its rows.
    const byTransaction: Record<string, PreviewableMessage[]> = {};
    for (const m of pending) {
      const group = byTransaction[m.transaction_uuid];
      if (group) group.push(m);
      else byTransaction[m.transaction_uuid] = [m];
    }

    Object.keys(byTransaction).forEach((transactionUuid) => {
      const group = byTransaction[transactionUuid];
      (async () => {
        try {
          const url = getApiUrl("APPOINTMENT_SCHEDULED_MESSAGES_PREVIEWS", "", {
            transactionUuid,
          });
          const data: AppointmentScheduledMessagePreview[] =
            await fetchWithAuth(url);
          const list = Array.isArray(data) ? data : [];
          const seen = new Set<string>();
          setPreviews((prev) => {
            const next = { ...prev };
            for (const entry of list) {
              if (!entry?.uuid) continue;
              seen.add(entry.uuid);
              next[entry.uuid] = { status: "ready", data: entry };
            }
            // Belt-and-braces: any row the backend omitted flips to error
            // rather than spinning in "loading" forever.
            for (const m of group) {
              if (!seen.has(m.uuid)) next[m.uuid] = { status: "error" };
            }
            return next;
          });
        } catch (err) {
          console.error("Failed to fetch scheduled-message previews:", err);
          // Whole transaction batch failed — mark its rows error so each shows
          // the inline fallback instead of spinning.
          setPreviews((prev) => {
            const next = { ...prev };
            for (const m of group) next[m.uuid] = { status: "error" };
            return next;
          });
        }
      })();
    });
  }, [messages, previews, fetchWithAuth]);

  return previews;
}
